/**
 * Element construction and escaping.
 *
 * The legacy file contains 135 `innerHTML = '...' + userData + '...'`
 * expressions. Each one is a place where a client's name — a value typed by a
 * stranger into a public form — becomes executable script. This module exists
 * so that never has to be written again: `h()` sets text through
 * `textContent`, which cannot execute anything, by construction.
 */

/**
 * Build an element.
 *
 * @param {string} tag
 * @param {Record<string, any>} [attrs] - `class`, `data-*`, `onclick` (as a
 *   function), `style` (string or object), and any other attribute.
 * @param {...(Node|string|number|null|undefined|false|any[])} children - Strings
 *   are inserted as TEXT, never parsed as markup. Arrays are flattened, so
 *   `...items.map(...)` and `items.map(...)` both work.
 * @returns {HTMLElement}
 */
export function h(tag, attrs, ...children) {
  /* SVG has its own namespace. `createElement('svg')` returns an
     HTMLUnknownElement — it appends without error and draws nothing, which is
     the kind of failure you only find by looking. Added 20 Aug 2026 with the
     assistant's launcher, whose mark is a four-point star path in the
     original (app.html:14503). */
  const el = SVG_TAGS.has(tag)
    ? document.createElementNS('http://www.w3.org/2000/svg', tag)
    : document.createElement(tag)

  for (const [key, value] of Object.entries(attrs || {})) {
    if (value == null || value === false) continue

    if (key === 'class' || key === 'className') {
      /* `className` on an SVG element is a read-only SVGAnimatedString. */
      el.setAttribute('class', String(value))
    } else if (key === 'style' && typeof value === 'object') {
      /* Custom properties have to go through `setProperty`. `Object.assign`
         onto a CSSStyleDeclaration walks its named properties, and `--cal-col`
         is not one of them, so the assignment is DROPPED — no error, no
         warning, no style.

         Trey, 30 Aug 2026: the calendar's Day width control drew, counted
         "2 of 5", and changed nothing, because the width was handed to `h()`
         as `style: { '--cal-col': '180px' }` and never arrived. Confirmed in
         his browser: Object.assign left the property empty, setProperty set
         it. Ordinary properties keep the assignment path they always had. */
      for (const [prop, val] of Object.entries(value)) {
        if (prop.startsWith('--')) el.style.setProperty(prop, String(val))
        else el.style[prop] = val
      }
    } else if (key.startsWith('on') && typeof value === 'function') {
      el.addEventListener(key.slice(2).toLowerCase(), value)
    } else if (key.startsWith('on')) {
      /* An `on*` key holding anything but a function used to fall through to
         `setAttribute`, which writes the value's STRING into the DOM as an
         inline handler — `onclick="[object Object]"` — and the browser then
         compiles that as source on every matching event.
 
         7 Sep 2026: `notes-view.js` passed `onclick: overlayCloser(...)`, and
         `overlayCloser` returns an OBJECT of two handlers meant to be spread.
         Eight other call sites spread it correctly; the ninth did not, and the
         suite passed on the machine it was written on. CI found it, as a
         `SyntaxError: Unexpected identifier 'Object'` from inside jsdom.
 
         Thrown rather than logged: a handler that was silently never attached
         is a button that does nothing, which is found by a person clicking it
         and giving up. */
      throw new TypeError(
        `h(): '${key}' must be a function, got ${typeof value}. ` +
          'A helper that returns several handlers — overlayCloser — is spread, not assigned.'
      )
    } else if (key === 'dataset' && typeof value === 'object') {
      Object.assign(el.dataset, value)
    } else {
      el.setAttribute(key, String(value))
    }
  }

  appendChildren(el, children)
  /* Declared `HTMLElement` and cast, rather than widened to a union. An SVG
     node is only ever a leaf here — a mark inside a button — and widening the
     return type would make every one of the several hundred call sites in this
     app take a type it never uses. */
  return /** @type {HTMLElement} */ (/** @type {unknown} */ (el))
}

/**
 * The SVG elements anything in this app draws with. Kept as a list rather than
 * a guess at the tag name, so an ordinary `<a>` or `<title>` is unaffected.
 */
const SVG_TAGS = new Set(['svg', 'path', 'circle', 'rect', 'line', 'polyline', 'polygon', 'g', 'defs', 'linearGradient', 'stop'])

/**
 * Append children to a node, flattening arrays and skipping empty values.
 * @param {Node} parent
 * @param {any[]} children
 */
export function appendChildren(parent, children) {
  for (const child of children.flat(Infinity)) {
    if (child == null || child === false || child === '') continue
    parent.appendChild(child instanceof Node ? child : document.createTextNode(String(child)))
  }
}

/**
 * Remove every child of a node. Replaces `el.innerHTML = ''`.
 * @param {Element|null} el
 * @returns {Element|null}
 */
export function clear(el) {
  if (!el) return el
  while (el.firstChild) el.removeChild(el.firstChild)
  return el
}

/**
 * Replace a node's contents with new children, in one step.
 * @param {Element|null} el
 * @param {...any} children
 */
export function replace(el, ...children) {
  if (!el) return el
  clear(el)
  appendChildren(el, children)
  return el
}

/**
 * Escape a string for the rare case where markup really must be assembled as
 * text — an email body, a CSV cell, an exported HTML document.
 *
 * This is NOT a licence to go back to innerHTML. If you are building DOM, use
 * `h()`.
 *
 * @param {unknown} value
 * @returns {string}
 */
export function escapeHtml(value) {
  return String(value == null ? '' : value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
}

/**
 * Scoped query. Prefer this and element references over getElementById —
 * global ids are how an element 120 lines away silently breaks unrelated UI.
 *
 * @param {string} selector
 * @param {ParentNode} [root]
 * @returns {HTMLElement|null}
 */
export function qs(selector, root) {
  return /** @type {HTMLElement|null} */ ((root || document).querySelector(selector))
}

/**
 * @param {string} selector
 * @param {ParentNode} [root]
 * @returns {HTMLElement[]}
 */
export function qsa(selector, root) {
  return Array.from((root || document).querySelectorAll(selector))
}
